import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';

import { IssueApiService } from '../issue.service';
import { BreadcrumbComponent, BreadcrumbCrumb } from '../../../shared/breadcrumb/breadcrumb.component';
import { IssueWorkflowApiService } from '../../issue-workflows/issue-workflow.service';
import { PmAdvancedApiService } from '../../pm-advanced/pm-advanced.service';
import { ProjectVersionDto, IssueCustomFieldValueDto } from '../../pm-advanced/pm-advanced.models';
import { TimeTrackingService } from '../../time-tracking/time-tracking.service';
import { TimeEntryDto, TIME_ENTRY_STATUS_LABELS } from '../../time-tracking/time-tracking.models';
import { AuthService } from '../../../core/services/auth.service';
import { ApiService } from '../../../core/services/api.service';
import { deadlineBadgeClass, deadlineDisplayLabel } from '../../shared/deadline.utils';
import {
  IssueDetailDto,
  IssueLabelDto,
  ProjectComponentDto,
  IssueHistoryDto,
  IssueActivityDto,
  IssueSummaryDto,
  IssueWatcherDto,
  IssueChecklistItemDto,
  IssueDependenciesDto,
  IssueLinkDto,
  ISSUE_TYPE_LABELS,
  ISSUE_STATUS_LABELS,
  ISSUE_PRIORITY_LABELS,
  ISSUE_SEVERITY_LABELS,
  ISSUE_RISK_LEVEL_LABELS,
  ISSUE_LINK_TYPE_LABELS,
  ISSUE_LINK_STATUS_LABELS
} from '../issue.models';
import { AvailableTransitionDto } from '../../issue-workflows/issue-workflow.models';
import { PmAiApiService } from '../../pm-ai/pm-ai.service';
import { MarkdownPipe } from '../../../shared/pipes/markdown.pipe';

interface EmployeeOption { id: string; name: string; }
interface IssueOption { id: string; key: string; summary: string; type: number; }
interface DuplicateHit { id: string; key: string; summary: string; similarity: number; }

@Component({
  selector: 'app-issue-detail',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule,
    MatCardModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule,
    MatSelectModule, MatProgressSpinnerModule, MatChipsModule, MatDividerModule, MatTooltipModule,
    MatTabsModule, MatProgressBarModule, MatCheckboxModule, MatTableModule,
    BreadcrumbComponent, MarkdownPipe
  ],
  templateUrl: './issue-detail.component.html',
  styleUrl: './issue-detail.component.scss'
})
export class IssueDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly issuesApi = inject(IssueApiService);
  private readonly workflowsApi = inject(IssueWorkflowApiService);
  private readonly pmAdvanced = inject(PmAdvancedApiService);
  private readonly pmAi = inject(PmAiApiService);
  private readonly timeApi = inject(TimeTrackingService);
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly aiBusy = signal(false);
  readonly error = signal<string | null>(null);
  readonly issue = signal<IssueDetailDto | null>(null);
  readonly employees = signal<EmployeeOption[]>([]);
  readonly labels = signal<IssueLabelDto[]>([]);
  readonly components = signal<ProjectComponentDto[]>([]);
  readonly projectIssues = signal<IssueOption[]>([]);
  readonly history = signal<IssueHistoryDto[]>([]);
  readonly activity = signal<IssueActivityDto[]>([]);
  readonly epicChildren = signal<IssueSummaryDto[]>([]);
  readonly availableTransitions = signal<AvailableTransitionDto[]>([]);
  readonly watchers = signal<IssueWatcherDto[]>([]);
  readonly checklist = signal<IssueChecklistItemDto[]>([]);
  readonly isWatching = signal(false);
  readonly issueTimeEntries = signal<TimeEntryDto[]>([]);
  readonly issueLoggedHours = signal(0);
  readonly loadingTimeEntries = signal(false);
  readonly projectVersions = signal<ProjectVersionDto[]>([]);
  readonly customFieldValues = signal<IssueCustomFieldValueDto[]>([]);
  readonly savingCustomFields = signal(false);
  readonly dependencies = signal<IssueDependenciesDto | null>(null);
  customFieldDraft: Record<string, string> = {};

  summary = '';
  description = '';
  acceptanceCriteria = '';
  definitionOfDone = '';
  type = 0;
  status = 0;
  priority = 2;
  severity: number | '' = '';
  riskLevel = 0;
  actualHours: number | null = null;
  storyPoints: number | null = null;
  originalEstimateHours: number | null = null;
  remainingEstimateHours: number | null = null;
  assigneeId = '';
  epicId = '';
  dueDate = '';
  startDate = '';
  color = '#64748b';
  descriptionPreview = false;
  showMergePanel = false;
  mergeTargetId = '';
  mergeSoftDelete = true;
  mergeCopyComments = false;
  readonly duplicateHits = signal<DuplicateHit[]>([]);
  selectedLabelIds: string[] = [];
  selectedComponentIds: string[] = [];
  selectedFixVersionIds: string[] = [];
  selectedAffectsVersionIds: string[] = [];
  newComment = '';
  replyToCommentId: string | null = null;
  replyBody = '';
  readonly reactionEmojis = ['👍', '👎', '🎉', '❤️', '👀'];
  newLabelName = '';
  newComponentName = '';
  newSubtaskSummary = '';
  linkTargetId = '';
  linkType = 0;
  linkReason = '';
  linkStatus = 0;
  attachmentName = '';
  attachmentUrl = '';
  transitionComment = '';
  newChecklistText = '';
  manualLogHours: number | null = null;
  manualLogDescription = '';
  manualLogDate = new Date().toISOString().slice(0, 10);

  readonly timeStatusLabels = TIME_ENTRY_STATUS_LABELS;
  readonly worklogColumns = ['date', 'hours', 'status', 'description'];

  readonly typeOptions = Object.entries(ISSUE_TYPE_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly statusOptions = Object.entries(ISSUE_STATUS_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly priorityOptions = Object.entries(ISSUE_PRIORITY_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly severityOptions = Object.entries(ISSUE_SEVERITY_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly riskLevelOptions = Object.entries(ISSUE_RISK_LEVEL_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly linkTypeOptions = Object.entries(ISSUE_LINK_TYPE_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly linkStatusOptions = Object.entries(ISSUE_LINK_STATUS_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));

  get issueId(): string {
    return this.route.snapshot.paramMap.get('id') ?? '';
  }

  ngOnInit(): void {
    this.loadIssue();
    this.loadEmployees();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadIssue(): void {
    this.loading.set(true);
    this.error.set(null);
    this.issuesApi.getById(this.issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (issue) => {
        this.issue.set(issue);
        this.summary = issue.summary;
        this.description = issue.description ?? '';
        this.acceptanceCriteria = issue.acceptanceCriteria ?? '';
        this.definitionOfDone = issue.definitionOfDone ?? '';
        this.type = issue.type;
        this.status = issue.status;
        this.priority = issue.priority;
        this.severity = issue.severity ?? '';
        this.riskLevel = typeof issue.riskLevel === 'number'
          ? issue.riskLevel
          : this.riskLevelFromLabel(issue.riskLevel);
        this.actualHours = issue.actualHours ?? null;
        this.storyPoints = issue.storyPoints ?? null;
        this.originalEstimateHours = issue.originalEstimateHours ?? null;
        this.remainingEstimateHours = issue.remainingEstimateHours ?? null;
        this.assigneeId = issue.assigneeId ?? '';
        this.epicId = issue.epicId ?? '';
        this.dueDate = (issue.dueDate ?? '').toString().slice(0, 10);
        this.startDate = (issue.startDate ?? '').toString().slice(0, 10);
        this.color = issue.color || '#64748b';
        this.selectedLabelIds = (issue.labels ?? []).map(l => l.id);
        this.selectedComponentIds = (issue.components ?? []).map(c => c.id);
        this.checklist.set(issue.checklistItems ?? []);
        this.isWatching.set(!!issue.isWatching);
        this.loading.set(false);
        this.loadProjectMeta(issue.projectId);
        this.loadIssueVersions(issue.id);
        this.loadCustomFields(issue.id);
        this.loadTimeline(issue.id);
        this.loadDependencies(issue.id);
        this.loadTransitions(issue.id);
        this.loadWatchers(issue.id);
        this.loadIssueTimeEntries(issue.id);
        if (!issue.checklistItems?.length) {
          this.loadChecklist(issue.id);
        }
        if (issue.type === 3) {
          this.loadEpicChildren(issue.id);
        }
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load issue.');
        this.loading.set(false);
      }
    });
  }

  loadProjectMeta(projectId: string): void {
    this.issuesApi.listLabels(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.labels.set(Array.isArray(items) ? items : []),
      error: () => this.labels.set([])
    });
    this.issuesApi.listComponents(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.components.set(Array.isArray(items) ? items : []),
      error: () => this.components.set([])
    });
    this.pmAdvanced.listVersions(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.projectVersions.set(Array.isArray(items) ? items : []),
      error: () => this.projectVersions.set([])
    });
    this.issuesApi.list({ projectId, page: 1, pageSize: 100 }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page) => this.projectIssues.set((page.items ?? []).map(i => ({
        id: i.id, key: i.key, summary: i.summary, type: i.type
      }))),
      error: () => this.projectIssues.set([])
    });
  }

  loadCustomFields(issueId: string): void {
    this.pmAdvanced.getIssueCustomFields(issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        const values = Array.isArray(items) ? items : [];
        this.customFieldValues.set(values);
        this.customFieldDraft = {};
        for (const field of values) {
          this.customFieldDraft[field.fieldDefinitionId] = field.valueText ?? field.displayValue ?? field.valueJson ?? '';
        }
      },
      error: () => {
        this.customFieldValues.set([]);
        this.customFieldDraft = {};
      }
    });
  }

  saveCustomFields(): void {
    const current = this.issue();
    if (!current) return;
    this.savingCustomFields.set(true);
    const values = this.customFieldValues().map(field => ({
      fieldDefinitionId: field.fieldDefinitionId,
      valueText: this.customFieldDraft[field.fieldDefinitionId] || undefined,
      valueNumber: field.fieldType === 1 ? Number(this.customFieldDraft[field.fieldDefinitionId]) || undefined : undefined,
      valueDate: field.fieldType === 2 ? this.customFieldDraft[field.fieldDefinitionId] || undefined : undefined,
      valueJson: field.fieldType >= 3 ? this.customFieldDraft[field.fieldDefinitionId] || undefined : undefined
    }));
    this.pmAdvanced.setIssueCustomFields(current.id, values).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.savingCustomFields.set(false);
        this.loadCustomFields(current.id);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to save custom fields.');
        this.savingCustomFields.set(false);
      }
    });
  }

  customFieldInputType(fieldType: number): string {
    if (fieldType === 1) return 'number';
    if (fieldType === 2) return 'date';
    return 'text';
  }

  loadIssueVersions(issueId: string): void {
    this.pmAdvanced.getIssueVersions(issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (links) => {
        const items = Array.isArray(links) ? links : [];
        this.selectedFixVersionIds = items.filter(l => l.relation === 1).map(l => l.versionId);
        this.selectedAffectsVersionIds = items.filter(l => l.relation === 0).map(l => l.versionId);
      },
      error: () => {
        this.selectedFixVersionIds = [];
        this.selectedAffectsVersionIds = [];
      }
    });
  }

  saveVersions(): void {
    const current = this.issue();
    if (!current) return;
    this.pmAdvanced.setIssueVersions(current.id, {
      fixVersionIds: this.selectedFixVersionIds,
      affectsVersionIds: this.selectedAffectsVersionIds
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadTimeline(current.id),
      error: (err) => this.error.set(err?.message || 'Failed to update versions.')
    });
  }

  loadTimeline(issueId: string): void {
    this.issuesApi.getHistory(issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.history.set(Array.isArray(items) ? items : []),
      error: () => this.history.set([])
    });
    this.issuesApi.getActivity(issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.activity.set(Array.isArray(items) ? items : []),
      error: () => this.activity.set([])
    });
  }

  loadTransitions(issueId: string): void {
    this.workflowsApi.getAvailableTransitions(issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.availableTransitions.set(Array.isArray(items) ? items : []),
      error: () => this.availableTransitions.set([])
    });
  }

  runTransition(t: AvailableTransitionDto): void {
    const current = this.issue();
    if (!current) return;
    if (t.blockers?.length) {
      this.error.set(t.blockers.join(' '));
      return;
    }
    if (t.requiresComment && !this.transitionComment.trim()) {
      this.error.set('A comment is required for this transition.');
      return;
    }
    this.workflowsApi.executeTransition(current.id, t.transitionId, this.transitionComment.trim() || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.transitionComment = '';
          this.loadIssue();
        },
        error: (err) => this.error.set(err?.message || 'Transition failed.')
      });
  }

  loadEpicChildren(epicId: string): void {
    this.issuesApi.getEpicChildren(epicId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.epicChildren.set(Array.isArray(items) ? items : []),
      error: () => this.epicChildren.set([])
    });
  }

  loadWatchers(issueId: string): void {
    this.issuesApi.getWatchers(issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.watchers.set(Array.isArray(items) ? items : []),
      error: () => this.watchers.set([])
    });
  }

  loadChecklist(issueId: string): void {
    this.issuesApi.getChecklist(issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.checklist.set(Array.isArray(items) ? items : []),
      error: () => this.checklist.set([])
    });
  }

  private riskLevelFromLabel(label?: string): number {
    const entry = Object.entries(ISSUE_RISK_LEVEL_LABELS).find(([, l]) => l === label);
    return entry ? Number(entry[0]) : 0;
  }

  loadEmployees(): void {
    this.api.getPaged<EmployeeOption>('v1/employees', { page: 1, pageSize: 200 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.employees.set((page.items ?? []).map((e: any) => ({
          id: String(e.id ?? e.Id),
          name: e.fullName ?? e.FullName ?? `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim()
        }))),
        error: () => this.employees.set([])
      });
  }

  epicOptions(): IssueOption[] {
    const currentId = this.issue()?.id;
    return this.projectIssues().filter(i => i.type === 3 && i.id !== currentId);
  }

  linkTargetOptions(): IssueOption[] {
    const currentId = this.issue()?.id;
    return this.projectIssues().filter(i => i.id !== currentId);
  }

  goBack(): void {
    this.router.navigate(['/issues']);
  }

  openIssue(id: string): void {
    this.router.navigate(['/issues', id]).then(() => this.loadIssue());
  }

  cloneIssue(): void {
    const current = this.issue();
    if (!current) return;
    this.saving.set(true);
    this.issuesApi.clone(current.id, { copyChecklist: true, createClonesLink: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cloned) => {
          this.saving.set(false);
          this.router.navigate(['/issues', cloned.id]);
        },
        error: (err) => {
          this.error.set(err?.message || 'Clone failed.');
          this.saving.set(false);
        }
      });
  }

  openMergePanel(): void {
    this.showMergePanel = !this.showMergePanel;
    this.mergeTargetId = '';
  }

  confirmMerge(): void {
    const current = this.issue();
    if (!current || !this.mergeTargetId || this.mergeTargetId === current.id) {
      this.error.set('Select a different target issue in the same project.');
      return;
    }
    if (!confirm(`Merge ${current.key} into the selected issue?`)) return;
    this.saving.set(true);
    this.issuesApi.merge(current.id, this.mergeTargetId, this.mergeSoftDelete, this.mergeCopyComments)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (target) => {
          this.saving.set(false);
          this.showMergePanel = false;
          this.router.navigate(['/issues', target.id]);
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set(err?.message || 'Merge failed.');
        }
      });
  }

  mergeInto(targetId: string): void {
    this.mergeTargetId = targetId;
    this.confirmMerge();
  }

  toggleArchive(): void {
    const current = this.issue();
    if (!current) return;
    const archive = !current.archivedAt;
    this.saving.set(true);
    this.issuesApi.archive(current.id, archive).pipe(takeUntil(this.destroy$)).subscribe({
      next: (issue) => {
        this.issue.set(issue);
        this.saving.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Archive failed.');
        this.saving.set(false);
      }
    });
  }

  save(): void {
    const current = this.issue();
    if (!current || !this.summary.trim()) return;
    if (!this.dueDate) {
      this.error.set('Deadline is required.');
      return;
    }
    if (this.startDate && this.dueDate < this.startDate) {
      this.error.set('Deadline cannot be earlier than start date.');
      return;
    }
    this.saving.set(true);
    this.issuesApi.update(current.id, {
      summary: this.summary.trim(),
      description: this.description.trim() || undefined,
      acceptanceCriteria: this.acceptanceCriteria.trim() || undefined,
      definitionOfDone: this.definitionOfDone.trim() || undefined,
      type: this.type,
      status: this.status,
      priority: this.priority,
      severity: this.severity !== '' ? this.severity : undefined,
      riskLevel: this.riskLevel,
      actualHours: this.actualHours ?? undefined,
      assigneeId: this.assigneeId || undefined,
      epicId: this.epicId || undefined,
      parentIssueId: current.parentIssueId,
      labelIds: this.selectedLabelIds,
      componentIds: this.selectedComponentIds,
      dueDate: this.dueDate,
      startDate: this.startDate || undefined,
      storyPoints: this.storyPoints ?? undefined,
      originalEstimateHours: this.originalEstimateHours ?? undefined,
      remainingEstimateHours: this.remainingEstimateHours ?? undefined,
      environment: current.environment,
      color: this.type === 3 ? this.color : (current.color ?? undefined)
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (issue) => {
        this.issue.set(issue);
        this.epicId = issue.epicId ?? '';
        this.dueDate = (issue.dueDate ?? '').toString().slice(0, 10);
        this.startDate = (issue.startDate ?? '').toString().slice(0, 10);
        this.saving.set(false);
        this.loadTimeline(issue.id);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to update issue.');
        this.saving.set(false);
      }
    });
  }

  assign(): void {
    const current = this.issue();
    if (!current) return;
    this.issuesApi.assign(current.id, this.assigneeId || null).pipe(takeUntil(this.destroy$)).subscribe({
      next: (issue) => {
        this.issue.set(issue);
        this.loadTimeline(issue.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to assign.')
    });
  }

  transition(status: number): void {
    const current = this.issue();
    if (!current) return;
    this.issuesApi.transition(current.id, status).pipe(takeUntil(this.destroy$)).subscribe({
      next: (issue) => {
        this.issue.set(issue);
        this.status = issue.status;
        this.loadTimeline(issue.id);
        this.loadTransitions(issue.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to transition.')
    });
  }

  linkEpic(): void {
    const current = this.issue();
    if (!current) return;
    this.issuesApi.linkToEpic(current.id, this.epicId || null).pipe(takeUntil(this.destroy$)).subscribe({
      next: (issue) => {
        this.issue.set(issue);
        this.epicId = issue.epicId ?? '';
        this.loadTimeline(issue.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to link epic.')
    });
  }

  addComment(): void {
    const current = this.issue();
    if (!current || !this.newComment.trim()) return;
    this.issuesApi.addComment(current.id, this.newComment.trim()).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.newComment = '';
        this.loadIssue();
      },
      error: (err) => this.error.set(err?.message || 'Failed to add comment.')
    });
  }

  startReply(commentId: string): void {
    this.replyToCommentId = commentId;
    this.replyBody = '';
  }

  cancelReply(): void {
    this.replyToCommentId = null;
    this.replyBody = '';
  }

  submitReply(): void {
    const current = this.issue();
    if (!current || !this.replyToCommentId || !this.replyBody.trim()) return;
    this.issuesApi.addComment(current.id, this.replyBody.trim(), this.replyToCommentId)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.cancelReply();
          this.loadIssue();
        },
        error: (err) => this.error.set(err?.message || 'Failed to add reply.')
      });
  }

  toggleReaction(commentId: string, emoji: string): void {
    const current = this.issue();
    if (!current) return;
    this.issuesApi.toggleCommentReaction(current.id, commentId, emoji)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: () => this.loadIssue(),
        error: (err) => this.error.set(err?.message || 'Failed to toggle reaction.')
      });
  }

  commentCount(comments: { replies?: unknown[] }[] | undefined): number {
    if (!comments?.length) return 0;
    return comments.reduce((n, c) => n + 1 + this.commentCount(c.replies as { replies?: unknown[] }[] | undefined), 0);
  }

  deleteComment(commentId: string): void {
    const current = this.issue();
    if (!current || !confirm('Delete this comment?')) return;
    this.issuesApi.deleteComment(current.id, commentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadIssue(),
      error: (err) => this.error.set(err?.message || 'Failed to delete comment.')
    });
  }

  createLabel(): void {
    const current = this.issue();
    if (!current || !this.newLabelName.trim()) return;
    this.issuesApi.createLabel(current.projectId, this.newLabelName.trim()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (label) => {
        this.newLabelName = '';
        this.labels.update(list => [...list, label]);
        this.selectedLabelIds = [...this.selectedLabelIds, label.id];
      },
      error: (err) => this.error.set(err?.message || 'Failed to create label.')
    });
  }

  createComponent(): void {
    const current = this.issue();
    if (!current || !this.newComponentName.trim()) return;
    this.issuesApi.createComponent(current.projectId, this.newComponentName.trim()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (component) => {
        this.newComponentName = '';
        this.components.update(list => [...list, component]);
        this.selectedComponentIds = [...this.selectedComponentIds, component.id];
      },
      error: (err) => this.error.set(err?.message || 'Failed to create component.')
    });
  }

  addSubtask(): void {
    const current = this.issue();
    if (!current || !this.newSubtaskSummary.trim()) return;
    this.issuesApi.createSubtask(current.id, {
      summary: this.newSubtaskSummary.trim(),
      priority: this.priority
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.newSubtaskSummary = '';
        this.loadIssue();
      },
      error: (err) => this.error.set(err?.message || 'Failed to create sub-task.')
    });
  }

  addLink(): void {
    const current = this.issue();
    if (!current || !this.linkTargetId) return;
    this.issuesApi.addLink(current.id, {
      targetIssueId: this.linkTargetId,
      linkType: this.linkType,
      reason: this.linkReason.trim() || null,
      status: this.linkStatus
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.linkTargetId = '';
        this.linkType = 0;
        this.linkReason = '';
        this.linkStatus = 0;
        this.loadIssue();
      },
      error: (err) => this.error.set(err?.message || 'Failed to add link.')
    });
  }

  updateLinkStatus(linkId: string, status: number): void {
    const current = this.issue();
    if (!current) return;
    this.issuesApi.updateLink(current.id, linkId, { status }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadIssue(),
      error: (err) => this.error.set(err?.message || 'Failed to update link.')
    });
  }

  loadDependencies(issueId: string): void {
    this.issuesApi.getDependencies(issueId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (deps) => this.dependencies.set(this.normalizeDependencies(deps)),
      error: () => this.dependencies.set({
        blockers: [],
        blockedBy: [],
        dependsOn: [],
        dependedOnBy: []
      })
    });
  }

  private normalizeDependencies(raw: IssueDependenciesDto | Record<string, unknown>): IssueDependenciesDto {
    const r = raw as Record<string, unknown>;
    const mapList = (v: unknown): IssueLinkDto[] =>
      (Array.isArray(v) ? v : []).map((item) => {
        const x = item as Record<string, unknown>;
        return {
          id: String(x['id'] ?? x['Id'] ?? ''),
          sourceIssueId: String(x['sourceIssueId'] ?? x['SourceIssueId'] ?? ''),
          sourceIssueKey: String(x['sourceIssueKey'] ?? x['SourceIssueKey'] ?? ''),
          targetIssueId: String(x['targetIssueId'] ?? x['TargetIssueId'] ?? ''),
          targetIssueKey: String(x['targetIssueKey'] ?? x['TargetIssueKey'] ?? ''),
          targetIssueSummary: String(x['targetIssueSummary'] ?? x['TargetIssueSummary'] ?? ''),
          linkType: Number(x['linkType'] ?? x['LinkType'] ?? 0),
          linkTypeLabel: String(x['linkTypeLabel'] ?? x['LinkTypeLabel'] ?? ''),
          isOutbound: Boolean(x['isOutbound'] ?? x['IsOutbound']),
          createdAt: (x['createdAt'] ?? x['CreatedAt']) as string | undefined,
          createdBy: (x['createdBy'] ?? x['CreatedBy']) as string | undefined,
          direction: (x['direction'] ?? x['Direction']) as string | undefined,
          reason: (x['reason'] ?? x['Reason']) as string | null | undefined,
          status: Number(x['status'] ?? x['Status'] ?? 0),
          statusLabel: String(x['statusLabel'] ?? x['StatusLabel'] ?? '')
        };
      });
    return {
      blockers: mapList(r['blockers'] ?? r['Blockers']),
      blockedBy: mapList(r['blockedBy'] ?? r['BlockedBy']),
      dependsOn: mapList(r['dependsOn'] ?? r['DependsOn']),
      dependedOnBy: mapList(r['dependedOnBy'] ?? r['DependedOnBy'])
    };
  }

  dependencyRows(): { title: string; items: IssueLinkDto[] }[] {
    const d = this.dependencies();
    if (!d) return [];
    return [
      { title: 'Blocked by', items: d.blockers },
      { title: 'Blocks', items: d.blockedBy },
      { title: 'Depends on', items: d.dependsOn },
      { title: 'Depended on by', items: d.dependedOnBy }
    ].filter((g) => g.items.length > 0);
  }

  /** SVG graph nodes/edges for Blocks + Depends On around the current issue. */
  dependencyGraph(): {
    width: number;
    height: number;
    nodes: { id: string; key: string; x: number; y: number; role: 'current' | 'upstream' | 'downstream' }[];
    edges: { x1: number; y1: number; x2: number; y2: number; label: string }[];
  } | null {
    const iss = this.issue();
    const d = this.dependencies();
    if (!iss || !d) return null;

    type Node = { id: string; key: string; x: number; y: number; role: 'current' | 'upstream' | 'downstream' };
    const upstream: { id: string; key: string; label: string }[] = [];
    const downstream: { id: string; key: string; label: string }[] = [];

    const pushUnique = (
      list: { id: string; key: string; label: string }[],
      id: string,
      key: string,
      label: string
    ) => {
      if (!id || id === iss.id || list.some(n => n.id === id)) return;
      list.push({ id, key, label });
    };

    for (const l of d.blockers) {
      pushUnique(upstream, l.isOutbound ? l.targetIssueId : l.sourceIssueId,
        l.isOutbound ? l.targetIssueKey : l.sourceIssueKey, 'blocks');
    }
    for (const l of d.dependsOn) {
      pushUnique(upstream, l.isOutbound ? l.targetIssueId : l.sourceIssueId,
        l.isOutbound ? l.targetIssueKey : l.sourceIssueKey, 'depends');
    }
    for (const l of d.blockedBy) {
      pushUnique(downstream, l.isOutbound ? l.targetIssueId : l.sourceIssueId,
        l.isOutbound ? l.targetIssueKey : l.sourceIssueKey, 'blocked');
    }
    for (const l of d.dependedOnBy) {
      pushUnique(downstream, l.isOutbound ? l.targetIssueId : l.sourceIssueId,
        l.isOutbound ? l.targetIssueKey : l.sourceIssueKey, 'depended');
    }

    if (!upstream.length && !downstream.length) return null;

    const colGap = 180;
    const rowGap = 56;
    const leftX = 70;
    const midX = leftX + colGap;
    const rightX = midX + colGap;
    const maxRows = Math.max(upstream.length, downstream.length, 1);
    const height = Math.max(120, 40 + maxRows * rowGap);
    const midY = height / 2;

    const nodes: Node[] = [
      { id: iss.id, key: iss.key, x: midX, y: midY, role: 'current' }
    ];
    upstream.forEach((n, i) => {
      const y = 40 + i * rowGap + (maxRows - upstream.length) * rowGap / 2;
      nodes.push({ id: n.id, key: n.key, x: leftX, y, role: 'upstream' });
    });
    downstream.forEach((n, i) => {
      const y = 40 + i * rowGap + (maxRows - downstream.length) * rowGap / 2;
      nodes.push({ id: n.id, key: n.key, x: rightX, y, role: 'downstream' });
    });

    const byId = new Map(nodes.map(n => [n.id, n]));
    const edges: { x1: number; y1: number; x2: number; y2: number; label: string }[] = [];
    const addEdge = (fromId: string, toId: string, label: string) => {
      const a = byId.get(fromId);
      const b = byId.get(toId);
      if (!a || !b) return;
      edges.push({ x1: a.x + 36, y1: a.y, x2: b.x - 36, y2: b.y, label });
    };

    for (const l of [...d.blockers, ...d.dependsOn]) {
      const otherId = l.isOutbound ? l.targetIssueId : l.sourceIssueId;
      addEdge(otherId, iss.id, l.linkTypeLabel || 'depends');
    }
    for (const l of [...d.blockedBy, ...d.dependedOnBy]) {
      const otherId = l.isOutbound ? l.targetIssueId : l.sourceIssueId;
      addEdge(iss.id, otherId, l.linkTypeLabel || 'blocks');
    }

    return { width: rightX + 80, height, nodes, edges };
  }

  removeLink(linkId: string): void {
    const current = this.issue();
    if (!current || !confirm('Remove this link?')) return;
    this.issuesApi.removeLink(current.id, linkId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadIssue(),
      error: (err) => this.error.set(err?.message || 'Failed to remove link.')
    });
  }

  addAttachment(): void {
    const current = this.issue();
    if (!current || !this.attachmentName.trim()) return;
    const name = this.attachmentName.trim();
    this.issuesApi.addAttachment(current.id, {
      fileName: name,
      originalFileName: name,
      contentType: 'application/octet-stream',
      sizeBytes: 0,
      storagePath: `issues/${current.id}/${name}`,
      url: this.attachmentUrl.trim() || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.attachmentName = '';
        this.attachmentUrl = '';
        this.loadIssue();
      },
      error: (err) => this.error.set(err?.message || 'Failed to add attachment.')
    });
  }

  removeAttachment(attachmentId: string): void {
    const current = this.issue();
    if (!current || !confirm('Remove this attachment?')) return;
    this.issuesApi.removeAttachment(current.id, attachmentId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadIssue(),
      error: (err) => this.error.set(err?.message || 'Failed to remove attachment.')
    });
  }

  toggleWatch(): void {
    const current = this.issue();
    if (!current) return;
    const req = this.isWatching()
      ? this.issuesApi.unwatch(current.id)
      : this.issuesApi.watch(current.id);
    req.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isWatching.update(v => !v);
        this.loadWatchers(current.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to update watch status.')
    });
  }

  aiSuggestAc(): void {
    const current = this.issue();
    if (!current) return;
    this.aiBusy.set(true);
    this.pmAi.acceptanceCriteria(current.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (s) => {
        this.aiBusy.set(false);
        try {
          const parsed = JSON.parse(s.outputJson);
          const lines: string[] = Array.isArray(parsed.acceptanceCriteria) ? parsed.acceptanceCriteria : [];
          this.acceptanceCriteria = lines.map(l => `- ${l}`).join('\n');
        } catch {
          this.error.set('Could not parse AC suggestion.');
        }
      },
      error: (err) => {
        this.aiBusy.set(false);
        this.error.set(err?.message || 'Suggest AC failed.');
      }
    });
  }

  aiPredictRisk(): void {
    const current = this.issue();
    if (!current) return;
    this.aiBusy.set(true);
    this.pmAi.predictRisk(current.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (s) => {
        this.aiBusy.set(false);
        try {
          const parsed = JSON.parse(s.outputJson);
          const label = parsed.riskLevelLabel || parsed.riskLevel;
          const reasons = Array.isArray(parsed.reasons) ? parsed.reasons.join('; ') : '';
          this.error.set(null);
          alert(`Predicted risk: ${label}${reasons ? '\n' + reasons : ''}`);
        } catch {
          alert(s.outputJson);
        }
      },
      error: (err) => {
        this.aiBusy.set(false);
        this.error.set(err?.message || 'Predict risk failed.');
      }
    });
  }

  aiFindDuplicates(): void {
    const current = this.issue();
    if (!current) return;
    this.aiBusy.set(true);
    this.duplicateHits.set([]);
    this.pmAi.duplicates(current.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (s) => {
        this.aiBusy.set(false);
        try {
          const parsed = JSON.parse(s.outputJson);
          const dups = Array.isArray(parsed.duplicates) ? parsed.duplicates : [];
          const hits: DuplicateHit[] = dups.map((d: any) => ({
            id: d.id || d.issueId || '',
            key: d.key || '',
            summary: d.summary || '',
            similarity: d.similarity || 0
          })).filter((d: DuplicateHit) => !!d.key);
          this.duplicateHits.set(hits);
          if (!hits.length) this.error.set('No similar open issues found.');
        } catch {
          this.error.set(s.outputJson);
        }
      },
      error: (err) => {
        this.aiBusy.set(false);
        this.error.set(err?.message || 'Find duplicates failed.');
      }
    });
  }

  addChecklistItem(): void {
    const current = this.issue();
    if (!current || !this.newChecklistText.trim()) return;
    this.issuesApi.addChecklistItem(current.id, this.newChecklistText.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.newChecklistText = '';
          this.loadChecklist(current.id);
        },
        error: (err) => this.error.set(err?.message || 'Failed to add checklist item.')
      });
  }

  toggleChecklistItem(item: IssueChecklistItemDto): void {
    const current = this.issue();
    if (!current) return;
    this.issuesApi.toggleChecklistItem(current.id, item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadChecklist(current.id),
      error: (err) => this.error.set(err?.message || 'Failed to update checklist item.')
    });
  }

  deleteChecklistItem(item: IssueChecklistItemDto): void {
    const current = this.issue();
    if (!current || !confirm('Delete this checklist item?')) return;
    this.issuesApi.deleteChecklistItem(current.id, item.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadChecklist(current.id),
      error: (err) => this.error.set(err?.message || 'Failed to delete checklist item.')
    });
  }

  transitionNeedsComment(t: AvailableTransitionDto): boolean {
    return t.requiresComment || t.blockers.some(b => /comment/i.test(b));
  }

  loadIssueTimeEntries(issueId: string): void {
    this.loadingTimeEntries.set(true);
    this.timeApi.list({ issueId, page: 1, pageSize: 100 }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page) => {
        const items = page.items ?? [];
        this.issueTimeEntries.set(items);
        const total = items.reduce((sum, e) => sum + (e.isRunning ? 0 : Number(e.hours ?? 0)), 0);
        this.issueLoggedHours.set(total);
        this.loadingTimeEntries.set(false);
      },
      error: () => {
        this.issueTimeEntries.set([]);
        this.issueLoggedHours.set(0);
        this.loadingTimeEntries.set(false);
      }
    });
  }

  startIssueTimer(): void {
    const current = this.issue();
    if (!current) return;
    this.timeApi.startTimer({ projectId: current.projectId, issueId: current.id, description: current.summary })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadIssueTimeEntries(current.id),
        error: (err) => this.error.set(err?.message || 'Failed to start timer.')
      });
  }

  pauseIssueTimer(entry: TimeEntryDto): void {
    const current = this.issue();
    if (!current) return;
    this.timeApi.pauseTimer(entry.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadIssueTimeEntries(current.id),
      error: (err) => this.error.set(err?.message || 'Failed to pause timer.')
    });
  }

  resumeIssueTimer(entry: TimeEntryDto): void {
    const current = this.issue();
    if (!current) return;
    this.timeApi.resumeTimer(entry.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadIssueTimeEntries(current.id),
      error: (err) => this.error.set(err?.message || 'Failed to resume timer.')
    });
  }

  stopIssueTimer(entry: TimeEntryDto): void {
    const current = this.issue();
    if (!current) return;
    this.timeApi.stopTimer(entry.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.loadIssueTimeEntries(current.id);
        this.loadIssue();
      },
      error: (err) => this.error.set(err?.message || 'Failed to stop timer.')
    });
  }

  addManualTimeLog(): void {
    const current = this.issue();
    if (!current || !this.manualLogHours || this.manualLogHours <= 0) return;
    this.timeApi.createManual({
      projectId: current.projectId,
      issueId: current.id,
      date: this.manualLogDate,
      hours: this.manualLogHours,
      description: this.manualLogDescription.trim() || current.key,
      isBillable: true
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.manualLogHours = null;
        this.manualLogDescription = '';
        this.loadIssueTimeEntries(current.id);
        this.loadIssue();
      },
      error: (err) => this.error.set(err?.message || 'Failed to add time log.')
    });
  }

  timeStatusLabel(status?: number): string {
    return this.timeStatusLabels[status ?? 0] ?? String(status);
  }

  issueBreadcrumbs(issue: IssueDetailDto): BreadcrumbCrumb[] {
    return [
      { label: 'Backlog', route: '/issues' },
      { label: issue.key }
    ];
  }

  statusBadgeClass(status: number): string {
    const map: Record<number, string> = {
      0: 'tt-badge--status-open',
      1: 'tt-badge--status-progress',
      2: 'tt-badge--status-review',
      3: 'tt-badge--status-done',
      5: 'tt-badge--status-blocked'
    };
    return map[status] ?? 'tt-badge--status-neutral';
  }

  priorityBadgeClass(priority: number): string {
    const map: Record<number, string> = {
      0: 'tt-badge--priority-lowest',
      1: 'tt-badge--priority-low',
      2: 'tt-badge--priority-medium',
      3: 'tt-badge--priority-high',
      4: 'tt-badge--priority-highest'
    };
    return map[priority] ?? 'tt-badge--priority-medium';
  }

  deadlineBadgeClass = deadlineBadgeClass;
  deadlineDisplayLabel = deadlineDisplayLabel;
}
